import React, { useState, useEffect, useRef, useMemo} from "react";
import {
  Upload,
  message,
  Spin,
  Empty,
  Modal
} from "antd";
import { PlusOutlined, BarsOutlined, LoadingOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";

import commonFn from "@/utils/commonFn";
import { acceptDict } from "@/utils/dict";

const CustomUpload = ({
  filePaths,
  setFilePaths,
  btnDisabled,
  setBtnDisabled,
  customConfig,
  isEdit,
  foreignId,
  vendorPaymentId,
  preCalFile,
  onFilesLoaded,
  type,
}) => {
  const [uploadFileList, setUploadFileList] = useState([]);
  const [pdfIndex, setPdfIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const controllersRef = useRef([])

  const createController = () => {
    const controller = new AbortController()
    controllersRef.current.push(controller)
    return controller
  }

   const cancelAllRequests = () => {
    controllersRef.current.forEach(c => c.abort())
    controllersRef.current = []
  }

  const deleteCommonFn = (filterFn) => {
    setPdfIndex(0)
    const list = uploadFileList.filter(filterFn)
    setFilePaths(list);
    setUploadFileList(list)
  }

  const deleteFn = async(file) => {
    // 删除-已有文件删除
    const { fileUploadId, uuid, uid, path } = file
    if(type === 'util_payment'){
      const params = fileUploadId ? {vendorPaymentId} : {foreignId}
      const res = await http.get(api.delFiles, {params:{uuid: uuid, path: path, ...params}});
      if(res.code === '1'){
        deleteCommonFn(item => item.uuid !== uuid)
        message.success("Delete success!");
      }
    } else if(type === 'pending_check'){
      // Existing pending-check attachments are reconciled atomically when the form is saved.
      deleteCommonFn(item => fileUploadId ? item.fileUploadId !== fileUploadId : item.uuid !== uuid)
    } else if(['util_payment_add', 'businessLicense'].includes(type)){
      deleteCommonFn(item => item.uid !== uid)
    }
  }

  const isFileAccepted = (file) => {
    if (customConfig?.allowAllFiles) return true;
    const defaultAccept = ["application/pdf"];

    // 使用配置的 accept 类型或默认值
    const acceptList = customConfig?.accept
      ? customConfig.accept.split(",").map(item => item.trim())
      : Object.keys(acceptDict).filter(key => defaultAccept.includes(acceptDict[key]));

    const mappedAcceptTypes = acceptList.map(type => acceptDict[type] || type);

    const isAccepted = mappedAcceptTypes.includes(file.type);

    if (!isAccepted) {
      // 找到 file.type 对应的 key，用于提示用户
      // const fallbackKey = Object.keys(acceptDict).find(key => acceptDict[key] === file.type);
      // const ext = fallbackKey?.split(".")[1]?.toUpperCase() || 'file';
      const ext = acceptList.join(',').replaceAll('.', ' ').toUpperCase() || 'file';
      message.error(`Please upload supported ${ext} file!`);
    }

    return isAccepted;
  }

  const uploadChangeFn = async({ file, fileList}) => {
    if(file.status === "removed"){
      Modal.confirm({
        title: "Delete File",
        content: "Do you want to delete this file?",
        okText: "Confirm",
        cancelText: "Cancel",
        onOk: async() => {
          deleteFn(file)
        },
        onCancel: () => {},
      });
      return
    }

    if (!isFileAccepted(file)) return;

    if(fileList[fileList.length - 1]?.uid === file.uid){
      setAddLoading(true);
      const newFileListTemp = fileList.filter((item) => uploadFileList.every((i) => i.uid !== item.uid)).map(k => ({...k, removeIconFlag: true}));
      addUpload(newFileListTemp)
    }
  };

  //添加时上传
  const addUpload = async (data) => {
    setBtnDisabled(true)
    const uploadedFiles = await commonFn.uploadFiles(data, {type, foreignId});
    const response = await http.post(api.getFile, uploadedFiles);
    const newListTemp = uploadedFiles.map((item, index) => ({
      uid: data[index]['uid'],
      originFileObj: data[index]['originFileObj'],
      ...item,
      removeIconFlag: true,
      base64: response.data[index].base64
    }))
    setAddLoading(false);
    setFilePaths([...filePaths, ...newListTemp]);
    setUploadFileList([...uploadFileList, ...newListTemp])
    setBtnDisabled(false)
    setRefreshKey(+new Date())
  };

  const fileToBase64Fn = async(data, removeIconFlag = false) => {
    const controller3 = createController()
    let response;
    try {
      response = await http.post(api.getFile, data || [], {signal: controller3.signal});
    } catch (error) {
      if (type !== 'pending_check') throw error;
    }
    // A failed preview must never turn existing pending-check attachments into an empty saved list.
    if (type === 'pending_check' && response?.code !== '1') {
      return (data || []).map(item => ({ ...item, removeIconFlag }));
    }
    let list = []
    if(response?.code === '1'){
      list = (data || [])?.map((item, index) => ({
        ...item,
        removeIconFlag,
        base64: response.data?.[index]?.base64
      }))
    }
    return list
  }

  const getFilePathsFn = async() => {
    const controller1 = createController()
    const controller2 = createController()
    if(!isEdit) return
    setIsLoading(true)
    setBtnDisabled(true)
    let oldListTemp = []
    let tenFileList = []
    if(['CheckApproval', 'ApprovalSummary', 'ApprovalHistory'].includes(customConfig?.pageName)){
      const response = await http.post(api.fileDetailsPayment, preCalFile);
      if(response?.code === '1'){
        oldListTemp = (response?.data || [])?.map((item, index) => ({
          ...item,
          removeIconFlag: true,
        }))
      }
    } else {
      if(['NewModal'].includes(customConfig?.pageName)){
        tenFileList = await http.get(api.UtilPaymentUtilFile, {params: {foreignId}, signal: controller1.signal }); 
      }
      const oldTemp = await http.get(api.getFilePaths, {params:{
        id: vendorPaymentId || foreignId,
        type
      }, signal: controller2.signal});
      if (type === 'pending_check' && oldTemp?.code !== '1') {
        onFilesLoaded?.(false);
        setIsLoading(false);
        return;
      }
      oldListTemp = await fileToBase64Fn([...(tenFileList || []), ...(oldTemp?.data || []) ], !customConfig?.oldStateRemoveIconFlag)
    }
    setFilePaths(oldListTemp);
    onFilesLoaded?.(true);
    setUploadFileList(oldListTemp)
    setIsLoading(false)
    setBtnDisabled(false)
    setRefreshKey(+new Date())
  }

  useEffect(()=>{
      getFilePathsFn()
    return () =>{
      cancelAllRequests()
      setUploadFileList([])
      setFilePaths([])
      setPdfIndex(0)
      setIsLoading(false)
      setBtnDisabled(false)
    }
  },[])

  const showUploadList  = {
    showPreviewIcon: (file) => false,
    showRemoveIcon: (file) => file?.removeIconFlag || false,
  }

  const base64ToUrlFn = (base64Data) => {
    const base64HeaderMatch = base64Data.match(/^data:(.*?);base64,/);
    let cleanBase64 = base64Data;
    if (base64HeaderMatch) {
        cleanBase64 = base64Data.replace(base64HeaderMatch[0], '');
    }
    // 将 Base64 转换为二进制数据
    const byteCharacters = atob(cleanBase64);
    const byteNumbers = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const url = window.URL.createObjectURL(new Blob([byteArray],{type: base64HeaderMatch?.[1] || 'application/pdf'}))
    return url
  }

  const previewUrl = useMemo(() => {
    if (!filePaths[pdfIndex]?.base64) return "";
    return base64ToUrlFn(filePaths[pdfIndex].base64);
  }, [filePaths, pdfIndex]);

  return (
    <>
      {isLoading ? 
      <div style={{width: '100%', height: '50vh', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        <Spin tip="Loading" size="large">
          <div style= {{ padding: 50 }} />
        </Spin> 
        {/* <Progress percent={per} status="active" /> */}
      </div>
      :
      <div key={refreshKey}>
        {
          !customConfig?.noShowUpload && (
          <Upload
              accept={customConfig?.allowAllFiles ? undefined : (customConfig?.accept || ".pdf")}
              listType="picture-card"
              fileList={uploadFileList}
              onChange={uploadChangeFn}
              beforeUpload={() => false}  // 阻止文件上传
              multiple={!customConfig?.notMultiple}
              disabled={customConfig?.disabled || btnDisabled}
              // ['showPreviewIcon', 'showRemoveIcon', 'showDownloadIcon', 'removeIcon' , 'downloadIcon']
              showUploadList={customConfig?.showUploadList || showUploadList}
          >
              {uploadFileList?.length >= (customConfig?.maxLength || 10000) ? null : (
                <button
                    style={{
                    border: 0,
                    background: 'none',
                    }}
                    type="button"
                >
                    {addLoading ? <LoadingOutlined /> : <PlusOutlined />}
                    <div
                    style={{
                        marginTop: 8,
                    }}
                    >
                    { customConfig?.name || 'Upload'}
                    </div>
                </button>
                )
              }
          </Upload>
          ) || null
        }
        {filePaths.length && filePaths[0].base64 && !customConfig?.noShowFile && (
          <>
            <div className="mainName">
              {filePaths.map((item, index) => (
                    <a
                      style={{width: '100%',  display: 'block', color: pdfIndex === index && '#000'}}
                      key={index}
                      onClick={() => setPdfIndex(index)}
                    >
                      <BarsOutlined /> {item.name}
                    </a>
                  ))
              }
            </div>
            <div className="PDFbox">
              <iframe
                src={previewUrl}
                // src={base64ToUrlFn(filePaths[pdfIndex]?.base64)}
                title="PDF Viewer"
                width="100%"
                style={{ height: customConfig?.pdfHeight || "50vh" }}
              />
            </div>
          </>
        ) || (
          (customConfig?.noShowUpload || customConfig?.disabled) ? (
            <Empty description="No data available" />
          ) : null
        )
        }
      </div>}
    </>
  );
};

export default CustomUpload;